import React, { Component } from 'react';
import { compose, graphql } from 'react-apollo';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import Button from 'shared/Button';
import Modal from 'shared/Modal';

import ForgotPasswordForm from './ForgotPasswordForm';

import { withAppContext } from '../contexts/app';
import { logInMutation } from 'apps/ask/mutations/log-in';
import { EMAIL_REGEX } from 'apps/ask/data/constants';

import SegmentAnalytics from '../../../helpers/SegmentAnalytics.js';

class SignInForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      error: '',
      isValid: false,
      isSubmitting: false,
      forgotPasswordModalIsOpen: false
    };
  }

  handleInputChange = (name) => (event) => {
    this.setState({ [name]: event.target.value }, this.validateForm);
  }

  handleSignUpClick = (event) => {
    event.preventDefault();
    const { onSignUpClick } = this.props;

    onSignUpClick && onSignUpClick();
  }

  handleForgotPasswordClick(event) {
    event.preventDefault();

    const { onForgotPasswordClick } = this.props;

    onForgotPasswordClick ? onForgotPasswordClick() : this.setState({ forgotPasswordModalIsOpen: true });
  }

  handleForgotPasswordModalClose() {
    this.setState({ forgotPasswordModalIsOpen: false });
  }

  validateForm() {
    this.setState({ isValid: this.isFormValid() });
  }

  isFormValid() {
    const { email, password } = this.state;

    if (!email || !EMAIL_REGEX.test(email)) return false;
    if (!password) return false;

    return true;
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    const { isValid, isSubmitting, email, password } = this.state;
    const { logInMutation, onSignIn } = this.props;
    const standardError = 'An unexpected error occured';

    if (!isValid || isSubmitting) return;

    try {
      this.setState({ isSubmitting: true, error: '' });

      const recaptchaToken = await this.getCaptchaToken();
      const response = await logInMutation({
        variables: {
          input: {
            email,
            password,
            recaptchaToken
          }
        }
      });
      const result = get(response, 'data.logIn');

      this.setState({ isSubmitting: false });

      if (result.success) {
        onSignIn(result.user);
      } else {
        this.setState({ error: result.notice || standardError });
        SegmentAnalytics.track('Sign in error', {
          error: result.notice
        });
      }
    } catch (error) {
      this.setState({ error: standardError, isSubmitting: false });
    }
  }

  async getCaptchaToken() {
    const { app } = this.props;

    return grecaptcha
      .execute(app.recaptchaSiteKey, { action: 'log_in' });
  }

  render() {
    const { error, isValid, isSubmitting, forgotPasswordModalIsOpen } = this.state;

    return (
      <div className="sign-in-form">
        { this.props.showTitle &&
          <h3>Sign in to your Triage account</h3>
        }

        <form onSubmit={this.handleSubmit} noValidate>
          {error && (
            <span className="form-message error">{error}</span>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              name="email"
              type="email"
              placeholder="Type your email"
              value={this.state.email}
              onChange={this.handleInputChange('email')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              name="password"
              type="password"
              placeholder="Type your password"
              value={this.state.password}
              onChange={this.handleInputChange('password')}
            />
          </div>

          { this.props.showSignUpLink &&
            <p className="sign-in-form__signup-link">
              Don’t have an account? <a href="#" onClick={this.handleSignUpClick.bind(this)}>Sign up here.</a>
            </p>
          }

          <p className="sign-in-form__forgot-password-link">
            Forgot your password? <a href="#" onClick={this.handleForgotPasswordClick.bind(this)}>Reset it here.</a>
          </p>

          <Button
            type="submit"
            disabled={!isValid}
            showSpinner={isSubmitting}
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>

        { forgotPasswordModalIsOpen &&
          <Modal
            className="forgot-password-modal"
            headerText="Reset Password"
            onModalClose={this.handleForgotPasswordModalClose.bind(this)}
          >
            <ForgotPasswordForm onRequestSent={this.handleForgotPasswordModalClose.bind(this)} />
          </Modal>
        }
      </div>
    );
  }
}

SignInForm.defaultProps = {
  showTitle: true,
  showSignUpLink: true
};

SignInForm.propTypes = {
  app: PropTypes.object.isRequired,
  showTitle: PropTypes.bool,
  showSignUpLink: PropTypes.bool,
  onSignIn: PropTypes.func.isRequired,
  onSignUpClick: PropTypes.func,
  onForgotPasswordClick: PropTypes.func,
  logInMutation: PropTypes.func.isRequired
};

export default compose(
  graphql(logInMutation, {
    name : 'logInMutation'
  }),
  withAppContext
)(SignInForm);
